import _, {chain} from 'lodash';
import {connect} from 'react-redux';

import {AsyncStatusesEnum} from '../../../../libs/shared/helpers/src/types/asyncStatuses';
import {
  AppParametricSelector,
  createSelector,
  makeMapStateToProps,
  wrapInDeepEqualSelector,
  wrapInShallowSelector,
} from '../../../../srcCommon/helpers/reselectHelpers';
import {reportToBugsnag} from '../../../../srcCommon/services/bugsnag/bugsnag';
import {
  serializeConversationForApplication,
  serializeSingleConversationForApplication,
} from '../../../helpers/models/applicationSerializationHelpers';
import {makeConversationActionContext} from '../../../helpers/models/conversationHelpers';
import {ConversationId} from '../../../models/api/conversationCommonTypes';
import {ConversationBaseModel, ConversationModel} from '../../../models/api/conversationModel';
import {
  LegacyConversationLinkDropdownEntryPointModel,
  LegacySidePanelEntryPointModel,
} from '../../../models/api/legacyEntryPointModel';
import {MessageStatusesEnum} from '../../../models/api/messageModel';
import {AppState} from '../../../store/appState';
import {makeGetLoadedDraftMessages} from '../../../store/data/conversations/conversationsSelectors';
import {getCustomFieldsForCards} from '../../../store/data/customFields/customFieldsSelectors';
import {getCards, getContacts, getTopics} from '../../../store/data/dataSelectors';
import {getInboxesTable} from '../../../store/data/inboxes/inboxesSelectors';
import {getTagsTable} from '../../../store/data/tags/tagsSelectors';
import {getFindTeammate, getFindTeammateByEmail} from '../../../store/data/teammates/lazyTeammatesSelectors';
import {getUserId} from '../../../store/data/teammates/teammatesSelectors';
import {CellsContext} from '../../../store/panels/cells/context/cellsContext';
import {ViewerPanelId} from '../../../store/panels/panelStateBase';
import {makeGetViewerPanel} from '../../../store/panels/viewer/viewerPanelSelectors';
import {ViewerPanelPageTypesEnum} from '../../../store/panels/viewer/viewerPanelState';
import {LegacyConversationsApplicationContextHost} from './legacyConversationsApplicationContextHost';

/*
 * Props.
 */

interface LegacyConversationsApplicationContextHostContainerProps {
  panelId: ViewerPanelId;
  entryPoint: LegacyConversationLinkDropdownEntryPointModel | LegacySidePanelEntryPointModel;
  onlyConversation: ConversationModel | undefined;
  conversations: ReadonlyArray<ConversationBaseModel>;
  cellsContext: CellsContext;
}

/*
 * Selectors.
 */

function getCellsContext(_state: AppState, props: LegacyConversationsApplicationContextHostContainerProps) {
  return props.cellsContext;
}

function getConversations(_state: AppState, props: LegacyConversationsApplicationContextHostContainerProps) {
  return props.conversations;
}

function getOnlyConversation(
  _state: AppState,
  props: LegacyConversationsApplicationContextHostContainerProps,
) {
  return props.onlyConversation;
}

function makeGetConversationIds() {
  return createSelector([getConversations], (conversations) => conversations.map((c) => c.id));
}

function makeGetLoadedViewerConversationId() {
  const getViewerPanel = makeGetViewerPanel();

  return createSelector([getViewerPanel], (viewerPanel) => {
    const currentPage = viewerPanel.pages.current;
    if (
      currentPage?.type !== ViewerPanelPageTypesEnum.SINGLE_CONVERSATION ||
      currentPage.status === AsyncStatusesEnum.LOADING
    ) {
      return undefined;
    }

    return currentPage.conversationId;
  });
}

/*
 * Single conversation selectors.
 */

function makeGetOnlyConversationLoadedDraftMessages() {
  // Preserve memoization by passing the conversation through the props.
  const getConversationFromProps = (_state: AppState, props: ConversationModel) => props;
  const getLoadedDraftMessages = makeGetLoadedDraftMessages<ConversationModel>(getConversationFromProps);

  // Condition selector to only run the base selector when the conversation is defined.
  return (state: AppState, props: LegacyConversationsApplicationContextHostContainerProps) => {
    const conversation = getOnlyConversation(state, props);
    return conversation && getLoadedDraftMessages(state, conversation);
  };
}

function makeGetUserFirstDraftMessageUid() {
  const getLoadedDraftMessages = makeGetOnlyConversationLoadedDraftMessages();
  return createSelector([getUserId, getLoadedDraftMessages], (userId, loadedDraftMessages) =>
    chain(loadedDraftMessages)
      .filter((d) => d.authorId === userId && d.status === MessageStatusesEnum.DRAFT_COMPOSE)
      .sortBy((d) => d.createdAt)
      .map((d) => d.uid)
      .find()
      .value(),
  );
}

function makeGetApplicationSingleConversation() {
  const getFirstDraftMessageUid = makeGetUserFirstDraftMessageUid();
  return createSelector(
    [
      getFindTeammate,
      getFindTeammateByEmail,
      getInboxesTable,
      getTagsTable,
      getContacts,
      getCards,
      getCustomFieldsForCards,
      getUserId,
      getOnlyConversation,
      getFirstDraftMessageUid,
      getTopics,
    ],
    (
      findTeammate,
      findTeammateByEmail,
      inboxes,
      tags,
      contacts,
      cards,
      customFields,
      userId,
      conversation,
      firstDraftMessageUid,
      topics,
    ) => {
      if (!conversation) {
        return undefined;
      }

      try {
        const data = {
          findTeammate,
          findTeammateByEmail,
          inboxes,
          tags,
          contacts,
          cards,
          customFields,
          topics,
        };
        return serializeSingleConversationForApplication(data, userId, conversation, firstDraftMessageUid);
      } catch (error) {
        reportToBugsnag(error);
        return undefined;
      }
    },
  );
}

/*
 * Multi-conversations selectors.
 */

function makeGetApplicationConversations() {
  return createSelector(
    [
      getFindTeammate,
      getFindTeammateByEmail,
      getInboxesTable,
      getTagsTable,
      getContacts,
      getCards,
      getCustomFieldsForCards,
      getUserId,
      getConversations,
      getTopics,
    ],
    (
      findTeammate,
      findTeammateByEmail,
      inboxes,
      tags,
      contacts,
      cards,
      customFields,
      userId,
      conversations,
      topics,
    ) => {
      if (conversations.length <= 1) {
        return [];
      }

      try {
        const data = {
          findTeammate,
          findTeammateByEmail,
          inboxes,
          tags,
          contacts,
          cards,
          customFields,
          topics,
        };
        return conversations.map((conversation) =>
          serializeConversationForApplication(data, userId, conversation),
        );
      } catch (error) {
        reportToBugsnag(error);
        return [];
      }
    },
  );
}

function makeGetConversationActionContexts() {
  return createSelector([getCellsContext, getConversations], (cellsContext, conversations) =>
    _.chain(conversations)
      .keyBy((conversation) => conversation.id)
      .mapValues((conversation) => makeConversationActionContext(cellsContext, conversation))
      .value(),
  );
}

function makeGetConversationNamespaces() {
  return createSelector([getConversations], (conversations) =>
    _.chain(conversations)
      .keyBy((conversation) => conversation.id)
      .mapValues((conversation) => conversation.namespace)
      .value(),
  );
}

function makeGetConversationInboxIds() {
  return createSelector([getConversations], (conversations) =>
    _.chain(conversations)
      .keyBy((conversation) => conversation.id)
      .mapValues((conversation) => conversation.inboxIds)
      .value(),
  );
}

function makeGetConversationPropFinder<V>(
  getConversationPropRecord: AppParametricSelector<
    LegacyConversationsApplicationContextHostContainerProps,
    Record<ConversationId, V | undefined>
  >,
) {
  return createSelector(
    [getConversationPropRecord],
    (conversationPropMap) => (conversationId: ConversationId) => conversationPropMap[conversationId],
  );
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<LegacyConversationsApplicationContextHostContainerProps>()(() => {
  const getApplicationSingleConversation = makeGetApplicationSingleConversation();
  const getConversationNamespaces = wrapInShallowSelector(makeGetConversationNamespaces());
  const getConversationActionContexts = wrapInShallowSelector(makeGetConversationActionContexts());
  const getConversationInboxIds = wrapInDeepEqualSelector(makeGetConversationInboxIds());

  return {
    conversationIds: wrapInShallowSelector(makeGetConversationIds()),
    singleConversation: wrapInDeepEqualSelector(getApplicationSingleConversation),
    conversations: wrapInDeepEqualSelector(makeGetApplicationConversations()),
    loadedViewerConversationId: makeGetLoadedViewerConversationId(),
    findConversationActionContext: makeGetConversationPropFinder(getConversationActionContexts),
    findConversationNamespace: makeGetConversationPropFinder(getConversationNamespaces),
    findConversationInboxIds: makeGetConversationPropFinder(getConversationInboxIds),
  };
});

export const LegacyConversationsApplicationContextHostContainer = connect(mapStateToProps)(
  LegacyConversationsApplicationContextHost,
);
